import { CreateWorkTemplatePayload } from 'ogp/services/work-template-service.types';
import { WorkTemplateDetail } from 'api';
import { FreelanceJobDetails, TempWorkJobDetails } from './utils.types';
import { WorkTemplateFormContext } from '../forms/shared/work-template-form.types';

export function encodeWorkTemplateToWorkTemplateForm(
  payload: WorkTemplateDetail
): WorkTemplateFormContext {
  if (!payload.freelanceJob && !payload.tempWorkJob && !payload.isEmployee) {
    throw new Error('Unknown employment type');
  }

  return <WorkTemplateFormContext>{
    workName: payload.name,
    shortDescription: payload.descriptionShort,
    descriptionLegalRequirements: payload.descriptionLawRequirements,
    longDescription: payload.descriptionLong,
    contactName: payload.contactName,
    contactPhoneWithPrefix: payload.contactPhoneNumber,
    location: payload.location,
    department: payload.departmentId,
    breakMinutes: payload.breakMinutes.toString(),
    imageUrl: payload.imageUrl ?? undefined,
    vat: payload.VAT,
    isFlexPoolOnly: payload.flexPoolOnly,
    costCenter: payload.costCenter ?? undefined,
    jobType: payload.jobTypeId,
    isTempworkOrtApplicable: payload.tempWorkJob?.ortApplicable,
    isFreelanceOrtApplicable: payload.freelanceJob?.ortApplicable,
    jobRequirements: payload.jobRequirements,
    requiredCertificates: payload.requiredCertificates,
    jobCertificates: payload.jobCertificates,
    flexPools: payload.flexPools,
    flexPool: undefined,
    hourlyRate: payload.freelanceJob?.hourlyRateCents
      ? (payload.freelanceJob.hourlyRateCents / 100).toString()
      : undefined,
    mutableUntilHours: payload.freelanceJob?.mutableUntilMinutes
      ? (payload.freelanceJob.mutableUntilMinutes / 60).toString()
      : undefined,
    cla: payload.tempWorkJob?.cla,
    isFreelance: !!payload.freelanceJob,
    isTempwork: !!payload.tempWorkJob,
    isEmployee: payload.isEmployee,
    startTime: payload.startTime ?? undefined,
    endTime: payload.endTime ?? undefined,
    maxClaimants: payload.maxClaimants ? payload.maxClaimants : undefined,
  };
}

export const decodeWorkTemplateFormToWorkTemplate = (
  payload: WorkTemplateFormContext
): CreateWorkTemplatePayload => {
  const {
    jobType,
    workName,
    shortDescription,
    descriptionLegalRequirements,
    contactName,
    contactPhoneWithPrefix,
    vat,
    breakMinutes,
    department,
    ...rest
  } = payload;
  const parsedBreakMinutes = Number(breakMinutes);
  if (
    jobType == null ||
    workName == null ||
    shortDescription == null ||
    descriptionLegalRequirements == null ||
    contactName == null ||
    contactPhoneWithPrefix == null ||
    parsedBreakMinutes == null ||
    isNaN(parsedBreakMinutes)
  ) {
    throw new Error('Missing required fields');
  }

  const result: CreateWorkTemplatePayload = {
    ...rest,
    departmentId: department,
    name: workName,
    location: payload.location ?? undefined,
    jobTypeId: jobType,
    descriptionShort: shortDescription,
    descriptionLawRequirements: descriptionLegalRequirements,
    contactName: contactName,
    contactPhoneNumber: contactPhoneWithPrefix,
    VAT: vat,
    flexPoolOnly: payload.isFlexPoolOnly,
    descriptionLong: payload.longDescription,
    breakMinutes: parsedBreakMinutes,
    jobCertificateIds: payload.jobCertificates?.map((certificate) => certificate.id) ?? [],
    jobRequirements: [
      ...(payload.jobRequirements?.skills ?? []),
      ...(payload.jobRequirements?.language ?? []),
      ...(payload.jobRequirements?.apparel ?? []),
    ],
    startTime: payload.startTime ?? null,
    endTime: payload.endTime ?? null,
    maxClaimants: payload.maxClaimants ? Number(payload.maxClaimants) : null,
  };

  if (payload.isFreelance) {
    const { hourlyRate, mutableUntilHours, isFreelanceOrtApplicable } = payload;
    const parsedHourlyRate = Number(hourlyRate);
    const parsedMutableUntilHours = Number(mutableUntilHours);
    if (
      parsedHourlyRate == null ||
      isNaN(parsedHourlyRate) ||
      parsedMutableUntilHours == null ||
      isNaN(parsedMutableUntilHours)
    ) {
      throw new Error('Missing required freelance fields');
    }
    result.freelanceJob = {
      hourlyRateCents: Math.round(parsedHourlyRate * 100),
      mutableUntilMinutes: Math.round(parsedMutableUntilHours * 60),
      ortApplicable: isFreelanceOrtApplicable,
    };
  } else {
    result.freelanceJob = null;
  }

  if (payload.isTempwork) {
    const { cla, isTempworkOrtApplicable } = payload;
    if (cla == null) {
      throw new Error('Missing required tempwork fields');
    }
    result.tempWorkJob = {
      cla,
      ortApplicable: isTempworkOrtApplicable,
    };
  } else {
    result.tempWorkJob = null;
  }

  return result;
};

export function isFreelanceWorkTemplate<
  T extends { freelanceJob?: FreelanceJobDetails | null | undefined },
>(job: T): job is T & { freelanceJob: FreelanceJobDetails } {
  return job.freelanceJob != null;
}

export function isTempWorkTemplate<
  T extends { tempWorkJob?: TempWorkJobDetails | null | undefined },
>(job: T): job is T & { tempWorkJob: TempWorkJobDetails } {
  return job.tempWorkJob != null;
}
